import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";

/**
 * Pick the PublishOS client a newsletter advertiser bills to.
 *
 * Type-to-search: PublishOS does the searching and returns at most 20
 * matches, so this works the same with 50 clients or 5,000. `value` is
 * {id, name} (id "" = not linked); `onChange` gets the same shape.
 */
export default function PublishOSClientPicker({ value, onChange, propertyName }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  // Wait for a pause in typing before asking PublishOS.
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);

  const { data: results = [], isFetching, error } = useQuery({
    queryKey: ["publishos-clients", q],
    queryFn: () => api.publishosClients(q),
    enabled: open && q.length >= 2,
    staleTime: 60_000,
  });

  const linked = !!value?.id;
  return (
    <div data-testid="publishos-client-picker" className="text-xs">
      <div className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)] mb-1">{tr("PublishOS client{value}", { value: propertyName ? ` (${propertyName})` : "" })}
      </div>
      {linked ? (
        <div className="flex items-center gap-2 border border-[var(--border)] px-2 py-1.5">
          <span className="flex-1 truncate text-[var(--ink)]">
            {value.name || value.id}
          </span>
          <button
            type="button"
            data-testid="publishos-client-unlink"
            onClick={() => onChange({ id: "", name: "" })}
            title={tr("Unlink")}
            className="text-[var(--ink-muted)] hover:text-red-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div>
          <input
            data-testid="publishos-client-search"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={tr("Type at least 2 letters of the client's name")}
            className="w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
          />
          {open && q.length >= 2 && (
            <div className="border border-t-0 border-[var(--border)] bg-white max-h-48 overflow-auto">
              {isFetching && (
                <div className="px-3 py-2 text-[var(--ink-muted)]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin inline" />{" "}{tr("Searching…")}</div>
              )}
              {!isFetching && error && (
                <div className="px-3 py-2 text-red-700">
                  {error?.response?.data?.detail || tr("Couldn't search PublishOS")}
                </div>
              )}
              {!isFetching && !error && results.length === 0 && (
                <div className="px-3 py-2 text-[var(--ink-muted)]">{tr("No client with that name in PublishOS.")}</div>
              )}
              {!isFetching &&
                results.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`publishos-client-option-${c.id}`}
                    onClick={() => {
                      onChange({ id: c.id, name: c.business_name });
                      setText("");
                      setOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-[var(--secondary)] text-[var(--ink)]"
                  >
                    {c.business_name}
                    {c.email ? (
                      <span className="text-[var(--ink-muted)]"> · {c.email}</span>
                    ) : null}
                  </button>
                ))}
            </div>
          )}
          <div className="text-[var(--ink-muted)] mt-1">{tr("Not linked — this advertiser's newsletter ads won't be booked in PublishOS.")}</div>
        </div>
      )}
    </div>
  );
}
